import { useState, useEffect } from "react";
import { fmt, fmtA } from "../../utils/helper";
import { Badge, KpiCard, ValidationDot } from "./SubComponents";
import { SectionHeader } from "./Header";
import { TxTable } from "./TxTable";
import { getCustomerAuditAPI } from "../../services/customerService";

// ── mock data (replace with real API call) ────────────────────────────────────
const MOCK = {
  customer: {
    _id: "cust_001",
    name: "Ahmed Raza Khan",
    phone: "03001234567",
    openingBalance: 50000,
    openingGoldWeight: 10.5,
    openingSilverWeight: 200,
    goldWeight: -8.25,
    goldAmount: 125000,
    silverWeight: 150,
    silverAmount: 45000,
    cash: 30000,
    balance: -95000,
    balanceType: "receivable",
  },
  transactions: {
    total: 6,
    receive: {
      totals: {
        goldWeight: 35.5,
        goldAmount: 280000,
        silverWeight: 300,
        silverAmount: 55000,
        cash: 80000,
        totalAmount: 415000,
      },
      rows: [
        {
          serialNo: 1001,
          date: "2024-01-05",
          description: "PC",
          weight: 10,
          amount: 80000,
          isSilver: false,
          isRateWork: false,
        },
        {
          serialNo: 1002,
          date: "2024-01-12",
          description: "Lab",
          weight: 8.5,
          amount: 68000,
          isSilver: false,
          isRateWork: false,
        },
        {
          serialNo: 1003,
          date: "2024-02-03",
          description: "Gold Rate Work (bar)",
          weight: 7,
          amount: 56000,
          isSilver: false,
          isRateWork: true,
        },
        {
          serialNo: 1004,
          date: "2024-02-20",
          description: "Silver",
          weight: 300,
          amount: 55000,
          isSilver: true,
          isRateWork: false,
        },
        {
          serialNo: 1005,
          date: "2024-03-01",
          description: "Bar",
          weight: 10,
          amount: 76000,
          isSilver: false,
          isRateWork: false,
        },
        {
          serialNo: 1006,
          date: "2024-03-10",
          description: "Cash",
          weight: 0,
          amount: 80000,
          isSilver: false,
          isRateWork: false,
        },
      ],
    },
    given: {
      totals: {
        goldWeight: 43.75,
        goldAmount: 155000,
        silverWeight: 150,
        silverAmount: 10000,
        cash: 50000,
        totalAmount: 215000,
      },
      rows: [
        {
          serialNo: 1001,
          date: "2024-01-05",
          description: "Bar",
          weight: 9,
          amount: 72000,
          isSilver: false,
          isRateWork: false,
        },
        {
          serialNo: 1003,
          date: "2024-02-03",
          description: "Coin",
          weight: 12,
          amount: 48000,
          isSilver: false,
          isRateWork: false,
        },
        {
          serialNo: 1004,
          date: "2024-02-20",
          description: "Silver",
          weight: 150,
          amount: 10000,
          isSilver: true,
          isRateWork: false,
        },
        {
          serialNo: 1005,
          date: "2024-03-01",
          description: "Cash",
          weight: 0,
          amount: 50000,
          isSilver: false,
          isRateWork: false,
        },
        {
          serialNo: 1006,
          date: "2024-03-10",
          description: "Ginni",
          weight: 22.75,
          amount: 35000,
          isSilver: false,
          isRateWork: false,
        },
      ],
    },
    net: {
      goldWeight: -8.25,
      goldAmount: 125000,
      silverWeight: 150,
      silverAmount: 45000,
      cash: 30000,
      totalAmount: 200000,
    },
  },
  grandNet: {
    goldWeight: 2.25,
    silverWeight: 350,
    balance: -95000,
    balanceType: "receivable",
  },
  validation: {
    goldWeightMatch: true,
    silverWeightMatch: true,
    balanceMatch: true,
  },
};

// ── main component ────────────────────────────────────────────────────────────
export default function CustomerAuditComponent({ customerId, from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("receive");

  useEffect(() => {
    // Replace this with your real API call:
    // const res = await fetch(`/api/customers/${customerId}/audit?from=${from}&to=${to}`);
    // const json = await res.json();
    // setData(json.data);
    if (customerId) {
      getCustomerAuditData();
    }
  }, [customerId, from, to]);

  async function getCustomerAuditData() {
    console.log({ customerId });
    try {
      setLoading(true);
      const response = await getCustomerAuditAPI({ customerId });
      console.log(response.data);
      if (response.success) {
        setData(response.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  if (loading)
    return (
      <div
        style={{
          padding: "3rem",
          textAlign: "center",
          color: "var(--color-text-secondary)",
          fontSize: 14,
        }}
      >
        Loading audit...
      </div>
    );

  if (!data) {
    return <h1>Data not found</h1>;
  }
  const { customer, transactions, grandNet, validation } = data;
  const c = customer;
  const initials = c.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const allValid =
    validation.goldWeightMatch &&
    validation.silverWeightMatch &&
    validation.balanceMatch;

  const netRows = [
    {
      label: "Gold weight",
      receive: transactions.receive.totals.goldWeight,
      given: transactions.given.totals.goldWeight,
      net: transactions.net.goldWeight,
      unit: "g",
      isAmount: false,
    },
    {
      label: "Gold amount",
      receive: transactions.receive.totals.goldAmount,
      given: transactions.given.totals.goldAmount,
      net: transactions.net.goldAmount,
      unit: "PKR",
      isAmount: true,
    },
    {
      label: "Silver weight",
      receive: transactions.receive.totals.silverWeight,
      given: transactions.given.totals.silverWeight,
      net: transactions.net.silverWeight,
      unit: "g",
      isAmount: false,
    },
    {
      label: "Silver amount",
      receive: transactions.receive.totals.silverAmount,
      given: transactions.given.totals.silverAmount,
      net: transactions.net.silverAmount,
      unit: "PKR",
      isAmount: true,
    },
    {
      label: "Cash",
      receive: transactions.receive.totals.cash,
      given: transactions.given.totals.cash,
      net: transactions.net.cash,
      unit: "PKR",
      isAmount: true,
    },
  ];

  const card = {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-tertiary)",
    borderRadius: "var(--border-radius-lg)",
    overflow: "hidden",
    marginBottom: 16,
  };

  const tabBtn = (key, label) => (
    <button
      onClick={() => setTab(key)}
      style={{
        padding: "8px 18px",
        fontSize: 13,
        fontWeight: tab === key ? 500 : 400,
        background:
          tab === key ? "var(--color-background-primary)" : "transparent",
        border: "none",
        borderBottom:
          tab === key ? "2px solid #a87506" : "2px solid transparent",
        color:
          tab === key
            ? "var(--color-text-primary)"
            : "var(--color-text-secondary)",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        fontFamily: "var(--font-sans)",
        maxWidth: 900,
        padding: "1rem 0",
      }}
    >
      {/* ── Customer header ── */}
      <div style={{ ...card }}>
        <div
          style={{
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "#FEF3C7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 500,
                fontSize: 15,
                color: "#92400E",
              }}
            >
              {initials}
            </div>
            <div>
              <div
                style={{
                  fontWeight: 500,
                  fontSize: 16,
                  color: "var(--color-text-primary)",
                }}
              >
                {c.name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--color-text-secondary)",
                  marginTop: 2,
                }}
              >
                {c.phone}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{ fontSize: 12, color: "var(--color-text-secondary)" }}
            >
              Overall balance
            </span>
            <span
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: "var(--color-text-primary)",
                fontFamily: "monospace",
              }}
            >
              {fmtA(Math.abs(c.balance))} PKR
            </span>
            <Badge type={c.balanceType} />
          </div>
        </div>

        {/* Opening balance bar */}
        <div
          style={{
            background: "var(--color-background-secondary)",
            borderTop: "0.5px solid var(--color-border-tertiary)",
            padding: "10px 20px",
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: "var(--color-text-secondary)",
              fontWeight: 500,
              letterSpacing: "0.05em",
              alignSelf: "center",
            }}
          >
            OPENING
          </span>
          {[
            { label: "Balance", value: `${fmtA(c.openingBalance)} PKR` },
            { label: "Gold weight", value: `${fmt(c.openingGoldWeight)} g` },
            {
              label: "Silver weight",
              value: `${fmt(c.openingSilverWeight)} g`,
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              style={{ display: "flex", gap: 6, alignItems: "baseline" }}
            >
              <span
                style={{ fontSize: 12, color: "var(--color-text-secondary)" }}
              >
                {label}:
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--color-text-primary)",
                  fontFamily: "monospace",
                }}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div
        style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}
      >
        <KpiCard
          label="Gold weight"
          value={`${fmt(c.goldWeight)} g`}
          sub={c.goldWeight < 0 ? "we hold their gold" : "customer holds"}
          color={c.goldWeight < 0 ? "#166534" : "#92400E"}
        />
        <KpiCard label="Gold amount" value={`${fmtA(c.goldAmount)} PKR`} />
        <KpiCard label="Silver weight" value={`${fmt(c.silverWeight)} g`} />
        <KpiCard label="Silver amount" value={`${fmtA(c.silverAmount)} PKR`} />
        <KpiCard label="Cash" value={`${fmtA(c.cash)} PKR`} />
      </div>

      {/* ── Receive / Given net summary ── */}
      <div style={{ ...card }}>
        <SectionHeader
          title="Transaction summary — receive vs given"
          color="var(--color-background-secondary)"
        />
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "24%" }} />
            </colgroup>
            <thead>
              <tr
                style={{
                  borderBottom: "0.5px solid var(--color-border-secondary)",
                }}
              >
                {["Field", "Receive", "Given", "Net"].map((h, i) => (
                  <th
                    key={i}
                    style={{
                      padding: "8px 14px",
                      textAlign: i === 0 ? "left" : "right",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "var(--color-text-secondary)",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {netRows.map((row, i) => {
                const netColor =
                  row.net > 0
                    ? "#166534"
                    : row.net < 0
                      ? "#991B1B"
                      : "var(--color-text-secondary)";
                return (
                  <tr
                    key={i}
                    style={{
                      borderBottom: "0.5px solid var(--color-border-tertiary)",
                      background:
                        i % 2 === 0
                          ? "transparent"
                          : "var(--color-background-secondary)",
                    }}
                  >
                    <td
                      style={{
                        padding: "9px 14px",
                        fontWeight: 500,
                        color: "var(--color-text-primary)",
                        fontSize: 13,
                      }}
                    >
                      {row.label}
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        fontFamily: "monospace",
                        color: "#166534",
                      }}
                    >
                      {row.isAmount ? fmtA(row.receive) : fmt(row.receive)}
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--color-text-tertiary)",
                          marginLeft: 4,
                        }}
                      >
                        {row.unit}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        fontFamily: "monospace",
                        color: "#991B1B",
                      }}
                    >
                      {row.isAmount ? fmtA(row.given) : fmt(row.given)}
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--color-text-tertiary)",
                          marginLeft: 4,
                        }}
                      >
                        {row.unit}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        fontFamily: "monospace",
                        fontWeight: 500,
                        color: netColor,
                      }}
                    >
                      {row.net > 0 ? "+" : ""}
                      {row.isAmount ? fmtA(row.net) : fmt(row.net)}
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--color-text-tertiary)",
                          marginLeft: 4,
                        }}
                      >
                        {row.unit}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Transaction detail tables ── */}
      <div style={{ ...card }}>
        <div
          style={{
            borderBottom: "0.5px solid var(--color-border-tertiary)",
            display: "flex",
            gap: 0,
          }}
        >
          {tabBtn("receive", `Receive (${transactions.receive.rows.length})`)}
          {tabBtn("given", `Given (${transactions.given.rows.length})`)}
        </div>
        <TxTable
          rows={
            tab === "receive"
              ? transactions.receive.rows
              : transactions.given.rows
          }
          side={tab}
        />
      </div>

      {/* ── Customer model snapshot vs computed ── */}
      <div style={{ ...card }}>
        <SectionHeader
          title="Model validation — stored vs computed from transactions"
          color={allValid ? "#DCFCE7" : "#FEF2F2"}
        />
        <div style={{ padding: "14px 16px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0,1fr))",
              gap: 10,
              marginBottom: 14,
            }}
          >
            {[
              { label: "Gold weight match", ok: validation.goldWeightMatch },
              {
                label: "Silver weight match",
                ok: validation.silverWeightMatch,
              },
              { label: "Balance match", ok: validation.balanceMatch },
            ].map(({ label, ok }) => (
              <div
                key={label}
                style={{
                  padding: "10px 14px",
                  borderRadius: "var(--border-radius-md)",
                  border: `0.5px solid ${ok ? "#86EFAC" : "#FCA5A5"}`,
                  background: ok ? "#F0FDF4" : "#FEF2F2",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <ValidationDot ok={ok} />
                <span
                  style={{
                    fontSize: 12,
                    color: ok ? "#166534" : "#991B1B",
                    fontWeight: 500,
                  }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col style={{ width: "30%" }} />
              <col style={{ width: "35%" }} />
              <col style={{ width: "35%" }} />
            </colgroup>
            <thead>
              <tr
                style={{
                  borderBottom: "0.5px solid var(--color-border-secondary)",
                }}
              >
                {[
                  "Field",
                  "Stored on customer model",
                  "Computed from transactions",
                ].map((h, i) => (
                  <th
                    key={i}
                    style={{
                      padding: "7px 12px",
                      textAlign: i === 0 ? "left" : "right",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "var(--color-text-secondary)",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                {
                  label: "Gold weight",
                  stored: fmt(c.goldWeight) + " g",
                  computed: fmt(grandNet.goldWeight) + " g",
                  match: validation.goldWeightMatch,
                },
                {
                  label: "Silver weight",
                  stored: fmt(c.silverWeight) + " g",
                  computed: fmt(grandNet.silverWeight) + " g",
                  match: validation.silverWeightMatch,
                },
                {
                  label: "Balance",
                  stored: fmtA(c.balance) + " PKR",
                  computed: fmtA(grandNet.balance) + " PKR",
                  match: validation.balanceMatch,
                },
              ].map((row, i) => (
                <tr
                  key={i}
                  style={{
                    borderBottom: "0.5px solid var(--color-border-tertiary)",
                    background: !row.match
                      ? "#FEF2F2"
                      : i % 2 === 0
                        ? "transparent"
                        : "var(--color-background-secondary)",
                  }}
                >
                  <td
                    style={{
                      padding: "8px 12px",
                      fontWeight: 500,
                      color: "var(--color-text-primary)",
                    }}
                  >
                    <ValidationDot ok={row.match} />
                    {row.label}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      textAlign: "right",
                      fontFamily: "monospace",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    {row.stored}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      textAlign: "right",
                      fontFamily: "monospace",
                      color: row.match
                        ? "var(--color-text-primary)"
                        : "#991B1B",
                      fontWeight: row.match ? 400 : 500,
                    }}
                  >
                    {row.computed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Footer ── */}
      <div
        style={{
          fontSize: 11,
          color: "var(--color-text-tertiary)",
          textAlign: "right",
          padding: "4px 0 1rem",
        }}
      >
        {transactions.total} transactions · data from customer model +
        transaction history
      </div>
    </div>
  );
}
